import React from "react";
import styled from "styled-components";
import { apiPost, apiDelete, apiPatch } from "../../api";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";

const Container = styled.div`
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  padding: ${(p) => (p.$compact ? "0.5rem" : "0.75rem")};
  margin: 0.5rem 0;
`;

const OptionButton = styled.button`
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.375rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--card);
  color: var(--foreground);
  font-size: 0.875rem;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--primary, #3b82f6);
    background: var(--accent, #f1f5f9);
  }
`;

const ResultRow = styled.div`
  position: relative;
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.375rem;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid ${(p) => (p.$selected ? "var(--primary, #3b82f6)" : "var(--border)")};
`;

const ResultFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: ${(p) => (p.$selected ? "rgba(59, 130, 246, 0.15)" : "var(--accent, #f1f5f9)")};
  transition: width 0.3s ease;
  border-radius: 0.5rem;
`;

const ResultText = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: var(--foreground);
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--muted-foreground, #64748b);
`;

const ActionLink = styled.button`
  background: none;
  border: none;
  color: var(--primary, #3b82f6);
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const PollDisplay = (props) => {
  const { poll: initialPoll, threadId, onPollUpdate, compact, authorId } = props;
  const currentUser = React.useContext(LoggedInUserContext);
  const [poll, setPoll] = React.useState(initialPoll);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setPoll(initialPoll);
  }, [initialPoll]);

  if (!poll) return null;

  const isAuthor = currentUser && authorId && currentUser.id === authorId;
  const hasVoted = !!poll.user_vote_option_id;
  const showResults = hasVoted || !poll.is_active;

  const handleVote = async (optionId) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await apiPost(`/api/v1/communities/threads/${threadId}/poll/vote`, {
        body: { option_id: optionId },
      });
      if (res.parsedResponse?.poll) {
        setPoll(res.parsedResponse.poll);
        onPollUpdate?.(res.parsedResponse.poll);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnvote = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await apiDelete(`/api/v1/communities/threads/${threadId}/poll/vote`);
      if (res.parsedResponse?.poll) {
        setPoll(res.parsedResponse.poll);
        onPollUpdate?.(res.parsedResponse.poll);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEndPoll = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await apiPatch(`/api/v1/communities/threads/${threadId}/poll/end`);
      if (res.parsedResponse?.poll) {
        setPoll(res.parsedResponse.poll);
        onPollUpdate?.(res.parsedResponse.poll);
      }
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = () => {
    if (poll.is_ended) return "Poll ended";
    if (!poll.is_active && poll.expires_at) return "Poll expired";
    if (poll.expires_at) {
      const diff = new Date(poll.expires_at) - new Date();
      if (diff <= 0) return "Poll expired";
      const hours = Math.floor(diff / 3600000);
      if (hours < 24) return `${hours}h left`;
      return `${Math.floor(hours / 24)}d left`;
    }
    return "Active";
  };

  return (
    <Container $compact={compact} onClick={(e) => e.stopPropagation()}>
      {showResults
        ? poll.options.map((opt) => (
            <ResultRow key={opt.id} $selected={opt.id === poll.user_vote_option_id}>
              <ResultFill $pct={opt.percentage} $selected={opt.id === poll.user_vote_option_id} />
              <ResultText>
                <span>
                  {opt.id === poll.user_vote_option_id && "✓ "}
                  {opt.text}
                </span>
                <span style={{ fontWeight: 500 }}>{opt.percentage}%</span>
              </ResultText>
            </ResultRow>
          ))
        : poll.options.map((opt) => (
            <OptionButton key={opt.id} onClick={() => handleVote(opt.id)} disabled={loading}>
              {opt.text}
            </OptionButton>
          ))}
      <Footer>
        <span>
          {poll.total_votes} vote{poll.total_votes !== 1 ? "s" : ""} · {statusLabel()}
        </span>
        <span>
          {hasVoted && poll.is_active && <ActionLink onClick={handleUnvote}>Undo vote</ActionLink>}
          {isAuthor && poll.is_active && !poll.is_ended && (
            <ActionLink onClick={handleEndPoll} style={{ marginLeft: hasVoted ? "0.5rem" : 0 }}>
              End poll
            </ActionLink>
          )}
        </span>
      </Footer>
    </Container>
  );
};

export default PollDisplay;
